import { computed, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { BalanceModel } from '../models/BalanceModel'
import { InventoryTransactionsModel } from '../models/InventoryTransactionsModel'
import { AllPartsModel } from '../models/AllParts'
import { hash } from '../utils'
import { useStoreProfiles } from './useStoreProfiles'
import { useUserStore } from './useUserStore'
import { useTaskProfiles } from './useTaskProfiles'
import { RaiHardwareCode } from './useCodes'
import { TransactionsRow } from 'app/types/database'
export interface InventoryBalance {
  id: number
  description: string
  pl_short_name: string
  pl_description: string
  quantity: number
  image: string
}
interface AddItemFields extends InventoryBalance {
  reason: string
  reason_name: string
  comment: string | null
  from?: string
}
export interface Balances {
  [part_id: string]: number
}
export interface PartTransaction {
  code: RaiHardwareCode
  task: any
  part_number: string | number
  reason: string
}
export type ImageLink = {
  id: string
  name: string
  link: string
}
const allParts = ref<any[]>([])
// const currentBalance = ref<InventoryBalance[]>([])
const balances = ref<Balances>({})
export const useInventory = defineStore('inventory', () => {
  const { writeBalances } = BalanceModel()
  const { writeParts } = AllPartsModel()
  const skipBalances = ref(false)
  const skipParts = ref(false)
  watch(
    balances,
    async (newData) => {
      if (skipBalances.value) {
        console.log('skipping balances write')
        skipBalances.value = false
      } else await writeBalances(newData).catch((error) => console.log(error))
    },
    {
      deep: true,
    }
  )

  watch(
    allParts,
    async (newData) => {
      console.log('writing all parts')
      if (skipParts.value) {
        console.log('skipping parts write')
        skipParts.value = false
      } else await writeParts(newData).catch((error) => console.log(error))
    },
    {
      deep: true,
    }
  )

  const setBalances = (data: Balances, options: { write?: boolean } = { write: true }) => {
    if (!options.write) {
      skipBalances.value = true
    }

    balances.value = data
  }
  const repHasItem = (part: any) => {
    return Boolean(balances.value[String(part.id)])
  }

  const { newTransaction } = InventoryTransactionsModel()
  const user = storeToRefs(useUserStore())

  //Creates an inventory transaction
  const updateBalance = async (data: {
    id: number // part_id
    quantity: number
    originalQuantity: number
    date: Date
    reason: any
    reason_name: any
    comment: any
    toRep?: any
    from?: any //store id
  }) => {
    if (data.reason == 'transfer') {
      await newTransaction({
        transaction_type: 'userToUser',
        from_entity: user.id.value,
        to_entity: data.toRep,
        part_number: data.id,
        quantity: data.quantity - data.originalQuantity,
        tasks_id: null,
        stores_id: null,
        collections_id: null,
        orders_id: null,
        reason: data.reason_name,
        reason_id: determineReasonId(data.reason, 'decrease'),
        comment: data.comment,
      }).then(() => {
        balances.value[String(data.id)] = Number(data.quantity)
      })
    } else if (data.reason == 'recoveredAtStore') {
      await newTransaction({
        transaction_type: 'storeToUser',
        from_entity: data.from,
        to_entity: user.id.value,
        part_number: data.id,
        quantity: data.quantity - data.originalQuantity,
        tasks_id: null,
        stores_id: null,
        collections_id: null,
        orders_id: null,
        reason: data.reason_name,
        reason_id: determineReasonId(data.reason, 'increase'),
        comment: data.comment,
      })
    } else if (Number(data.quantity) < Number(data.originalQuantity)) {
      await newTransaction({
        transaction_type: 'userToLost',
        from_entity: user.id.value,
        to_entity: null,
        part_number: data.id,
        quantity: data.quantity - data.originalQuantity,
        tasks_id: null,
        stores_id: null,
        collections_id: null,
        orders_id: null,
        reason: data.reason_name,
        reason_id: determineReasonId(data.reason, 'increase'),
        comment: data.comment,
      }).then(() => {
        balances.value[String(data.id)] = Number(data.quantity)
      })
    } else if (Number(data.quantity) > Number(data.originalQuantity)) {
      await newTransaction({
        transaction_type: 'addBOH',
        from_entity: data.from ? data.from : null,
        to_entity: user.id.value,
        part_number: data.id,
        quantity: data.quantity - data.originalQuantity,
        tasks_id: null,
        stores_id: null,
        collections_id: null,
        orders_id: null,
        reason: data.reason_name,
        reason_id: determineReasonId(data.reason, 'decrease'),
        comment: data.comment,
      }).then(() => {
        balances.value[String(data.id)] = Number(data.quantity)
      })
    }
  }
  const determineReasonId = (reason: string, type: 'increase' | 'decrease'): number => {
    if (type == 'increase') {
      switch (reason) {
        case 'cycleCount':
          return 1
        case 'transfer':
          return 2
        case 'autoEquipmentImport':
          return 3
        case 'others':
          return 4
        case 'recoveredAtStore':
          return 5
        default:
          return 4
      }
    } else {
      switch (reason) {
        case 'cycleCount':
          return 6
        case 'transfer':
          return 7
        case 'damaged':
          return 8
        case 'others':
          return 9
        default:
          return 9
      }
    }
  }
  const addItemToBalance = async (data: AddItemFields) => {
    await newTransaction({
      transaction_type: data.reason == 'recoveredAtStore' ? 'storeToUser' : 'addBOH',
      from_entity: data.from ? data.from : null,
      to_entity: user.id.value,
      part_number: data.id,
      quantity: data.quantity,
      tasks_id: null,
      stores_id: null,
      collections_id: null,
      orders_id: null,
      reason: data.reason_name,
      reason_id: determineReasonId(data.reason, 'increase'),
      comment: data.comment,
    }).then(() => {
      balances.value[String(data.id)] = Number(data.quantity)
    })
  }
  //use this one for hardware transactions
  //todo?: merge w/ update balance fn?
  const createPartTransaction = async (data: PartTransaction) => {
    const transactionCalc = transactionTypeHardwareTask(data.code, {
      part_number: data.part_number,
      user_id: user.id.value,
      tasks_id: data.task.task_id,
      store_id: data.task.store_id,
    })
    const applyToRepBalance = Number(data.code.qty_to_rep) != 0
    return newTransaction({
      transaction_type: transactionCalc.transaction_type,
      from_entity: transactionCalc.from_entity,
      to_entity: transactionCalc.to_entity,
      part_number: transactionCalc.part_number!,
      quantity: Number(transactionCalc.quantity),
      tasks_id: data.task.task_id,
      stores_id: data.task.store_id,
      collections_id: null,
      orders_id: data.task.orderline_id,
      reason: data.reason,
      reason_id: null,
      comment: null,
    }).then((uuid) => {
      if (applyToRepBalance) {
        if (!balances.value[String(data.part_number)]) {
          balances.value[String(data.part_number)] = 0
        }
        balances.value[String(data.part_number)] += Number(data.code.qty_to_rep)
      }
      return uuid
    })
  }
  const appliesToRep = (data: TransactionsRow) => {
    return data.from_entity == user.id.value || data.to_entity == user.id.value
  }
  //Use transaction information to undo changes to reps balance
  const undoPartTransaction = async (transaction: TransactionsRow) => {
    //undo inv balance changes if applies to rep
    if (appliesToRep(transaction)) {
      balances.value[String(transaction.part_number)] +=
        Math.abs(Number(transaction.quantity)) * (transaction.to_entity == user.id.value ? -1 : 1)

      //remove
      await InventoryTransactionsModel().removeTransaction(transaction.uuid)
    }
  }

  //Here either a rep trashes from store, recovers from store, or gives to store
  const transactionTypeHardwareTask = (
    code: RaiHardwareCode,
    data: {
      part_number?: null | string | number
      user_id: null | string
      tasks_id: null | string
      store_id?: null | string
    } = {
      part_number: null,
      user_id: null,
      tasks_id: null,
      store_id: null,
    }
  ): {
    transaction_type: TransactionsRow['transaction_type']
    from_entity: string
    to_entity: string | null
    part_number: string | number | null
    quantity: string
  } => {
    if (!Number(code.qty_to_rep)) {
      return {
        transaction_type: 'storeToLost',
        from_entity: data.store_id!,
        to_entity: null,
        part_number: data.part_number!,
        quantity: code.qty_to_trash,
      }
    } else if (Number(code.qty_to_rep) > 0) {
      return {
        transaction_type: 'storeToUser',
        from_entity: data.store_id!,
        to_entity: data.user_id!,
        part_number: data.part_number!,
        quantity: code.qty_to_rep,
      }
    } else {
      return {
        transaction_type: 'userToStore',
        from_entity: data.user_id!,
        to_entity: data.store_id!,
        part_number: data.part_number!,
        quantity: code.qty_to_store,
      }
    }
  }
  /**Inventory transaction not needed. Rep must first adjust balance to zero.
   * This is so reps dont accidentally remove inventory boh.
   */
  const removeItemFromBalance = async (data: { id: string | number }) => {
    //we won't allow removing from balance unless explicity set to zero first
    if (balances.value[String(data.id)] > 0) return
    //now remove it
    delete balances.value[String(data.id)]
  }

  /**
   * Returns inventory balance in table format
   */
  const getCurrentBalanceAsTable = computed(() => {
    const balancesHash = allParts.value.reduce((acc, curr) => {
      if (!(String(curr.id) in balances.value)) return acc
      const { id, image, part_number, description, ...pl_info } = curr
      if (!(String(id) in acc)) {
        acc[String(id)] = {
          id,
          image: image.length ? image[0] : null,
          part_number,
          description,
          quantity: balances.value[String(curr.id)],
          pl_info: [],
        }
      }

      if (pl_info.pl_id) {
        acc[String(id)].pl_info.push({ ...pl_info })
      }
      return acc
    }, {})
    const balancesData = Object.keys(balancesHash).map((key) => {
      return balancesHash[key]
    })

    const getOptions = () => {
      const out: any[] = []
      balancesData.map((row) => {
        for (const val of row.pl_info) {
          if (!out.includes(val.pl_short_name)) {
            out.push({
              en_US: val.pl_short_name,
              fr_CA: val.pl_short_name,
              es_MX: val.pl_short_name,
            })
          }
        }
      })
      return out
    }

    return {
      meta: {
        columns: {
          image: {
            label: { en_US: 'Image', es_MX: '', fr_CA: 'Image' },
            type: 'string',
            // hide: true
            hideInFilters: true,
          },
          part_number: {
            label: { en_US: 'Part number', es_MX: '', fr_CA: 'No de la pièce' },
            type: 'string',
          },
          description: {
            label: { en_US: 'Part Name', es_MX: '', fr_CA: 'Nom de la pièce' },
            type: 'string',
          },
          pl_info: {
            label: {
              en_US: 'Product Line(s)',
              es_MX: '',
              fr_CA: 'Gamme de produits',
            },
            type: 'array',
            hidden: true,
            hideInFilters: true,
            options: getOptions(),
            optionsKey: 'pl_short_name',
          },

          quantity: { label: { en_US: 'Quantity', es_MX: '', fr_CA: 'Quantité' }, type: 'number' },
        },
      },
      data: balancesData,
    }
  })

  const setAllParts = (data: any[], options: { write?: boolean } = { write: true }) => {
    if (!options.write) {
      skipParts.value = true
    }
    allParts.value = data
  }
  const partsHash = computed(() => hash(allParts.value))
  const getAllParts = computed(() => {
    const dataHash = allParts.value.reduce((acc, curr) => {
      if (String(curr.id) in balances.value) return acc
      const { id, image, part_number, description, ...pl_info } = curr
      if (!(String(id) in acc)) {
        acc[String(id)] = {
          id,
          image: image.length ? image[0] : null,
          part_number,
          description,
          quantity: balances.value[String(curr.id)],
          pl_info: [],
        }
      }

      if (pl_info.pl_id) {
        acc[String(id)].pl_info.push({ ...pl_info })
      }
      return acc
    }, {})
    const data = Object.keys(dataHash).map((key) => {
      return dataHash[key]
    })
    const getOptions = () => {
      const out: any[] = []
      data.map((row: any) => {
        for (const val of row.pl_info) {
          if (!out.includes(val.pl_short_name)) {
            out.push({
              en_US: val.pl_short_name,
              fr_CA: val.pl_short_name,
              es_MX: val.pl_short_name,
            })
          }
        }
      })
      return out
    }
    return {
      meta: {
        columns: {
          image: {
            label: { en_US: 'Image', es_MX: '', fr_CA: 'Image' },
            type: 'string',
            hideInFilters: true,
          },
          part_number: {
            label: { en_US: 'Part number', es_MX: '', fr_CA: 'No de la pièce' },
            type: 'string',
          },
          description: {
            label: { en_US: 'Part Name', es_MX: '', fr_CA: 'Nom de la pièce' },
            type: 'string',
          },
          pl_info: {
            label: {
              en_US: 'Product Line(s)',
              es_MX: '',
              fr_CA: 'Gamme de produits',
            },
            type: 'array',
            hidden: true,
            hideInFilters: true,
            options: getOptions(),
            optionsKey: 'pl_short_name',
          },
          // pl_short_name: {
          //   label: {
          //     en_US: 'Product Line Short Name',
          //     es_MX: '',
          //     fr_CA: 'Abréviation de la gamme de produits',
          //   },
          //   type: 'string',
          // },
          // pl_description: {
          //   label: {
          //     en_US: 'Product Line Description',
          //     es_MX: '',
          //     fr_CA: 'Description de la gamme de produits',
          //   },
          //   type: 'string',
          // },
        },
      },
      data: data,
    }
  })

  const getPartsByStoreID = (id: string | number) => {
    const { getStore } = useStoreProfiles()
    return {
      meta: {
        columns: {
          product_line_short_name: {
            label: { en_US: 'PL Short Name', es_MX: '', fr_CA: '' },
            type: 'string',
          },
          product_line_name: {
            label: { en_US: 'PL Name', es_MX: '', fr_CA: '' },
            type: 'string',
          },
          part_name: {
            label: { en_US: 'Part Name', es_MX: '', fr_CA: '' },
            type: 'string',
          },
          part_number: {
            label: { en_US: 'Part Number', es_MX: '', fr_CA: '' },
            type: 'string',
          },
        },
      },
      /**@ts-expect-error its actually an array */
      data: getStore(id)?.active_product_line?.map((row, index) => {
        return {
          ...row,
          _index: index,
        }
      }),
    }
  }
  const getInventorySelectionItemsByTaskId = (taskId: string | number) => {
    const productLineId = useTaskProfiles().getTaskByTaskId(taskId)?.product_line_id
    const partsIds = useTaskProfiles().getTaskByTaskId(taskId)?.inventory_item_ids
    console.log({ productLineId }, { partsIds })
    if (partsIds) {
      const parts = allParts.value.filter((part) => {
        return partsIds.includes(Number(part.id)) && productLineId == part.pl_id && repHasItem(part)
      })
      return parts.map((part) => {
        return { name: part.id, label: part.description }
      })
    } else {
      return []
    }
  }

  const resetStoreState = () => {
    skipBalances.value = true
    balances.value = {}
    skipParts.value = true
    allParts.value = []
  }

  return {
    getCurrentBalanceAsTable,
    updateBalance,
    createPartTransaction,
    addItemToBalance,
    removeItemFromBalance,
    setBalances,
    getAllParts,
    setAllParts,
    partsHash,
    getPartsByStoreID,
    getInventorySelectionItemsByTaskId,
    resetStoreState,
    undoPartTransaction,
  }
})
